/**
 * Settings Repository
 * Provides unified interface for settings and working patterns
 * Routes to API (live mode) or localStorage (demo mode) based on screen data mode
 */

import type { WorkingPattern } from '@/types/timesheet';
import { DEFAULT_TIMESHEET_SETTINGS, DEFAULT_ON_BEHALF_OF_SETTINGS } from '@/types/timesheetSettings';
import type { 
  OrganisationTimesheetSettings, 
  TimesheetDeadlineSettings, 
  UtilisationSettings,
  WorkingProfile,
  PolicyViolationSettings,
  PolicyLimits,
  ApprovalScreenSettings,
  OnBehalfOfSettings,
  OnBehalfOfRelationship,
} from '@/types/timesheetSettings';
import type { OnBehalfOfRelationshipImportRowDto } from '@/types/api';
import { workingPatternService } from './workingPatternService';
import { 
  getOrganisationSettings as getOrgSettingsLocal,
  saveOrganisationSettings as saveOrgSettingsLocal,
  updateDeadlineSettings as updateDeadlineLocal,
  updateUtilisationSettings as updateUtilisationLocal,
  updateWorkingHoursSettings as updateWorkingHoursLocal,
  updateOnBehalfOfSettings as updateOnBehalfOfLocal
} from './timesheetSettingsService';
import {
  MOCK_ENTRY_RELATIONSHIPS,
  MOCK_APPROVAL_RELATIONSHIPS,
  MOCK_PEOPLE
} from './onBehalfOfSettings.mock';
import * as timesheetSettingsApi from './api/timesheetSettingsApi';
import { normaliseOnBehalfOfSettings } from './api/mappers';
import * as workingPatternsApi from './api/workingPatternsApi';
import { prefixKey } from '@/utils/storagePrefix';

// Cache key for live mode organisation settings
const LIVE_SETTINGS_CACHE_KEY = prefixKey('timetrackpro:liveOrgSettings');


/**
 * Get cached live organisation settings from localStorage
 * This allows the first render to use the cached value instead of the default 40h
 */
export function getCachedLiveOrgSettings(): OrganisationTimesheetSettings | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(LIVE_SETTINGS_CACHE_KEY);
    return raw ? JSON.parse(raw) as OrganisationTimesheetSettings : null;
  } catch {
    return null;
  }
}

/**
 * Cache live organisation settings to localStorage
 * Called after successfully fetching settings from the API
 */
export function setCachedLiveOrgSettings(settings: OrganisationTimesheetSettings): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(LIVE_SETTINGS_CACHE_KEY, JSON.stringify(settings));
  } catch {
    // Swallow errors (e.g., localStorage full)
  }
}

/**
 * Get organisation default working pattern
 * @param mode - 'demo' for localStorage, 'live' for API
 */
export async function getOrganisationWorkingPattern(mode: 'demo' | 'live'): Promise<WorkingPattern> {
  if (mode === 'live') {
    return await workingPatternsApi.getOrganisationPattern();
  } else {
    return workingPatternService.getOrgDefault();
  }
}

/**
 * Set organisation default working pattern
 * @param pattern - The working pattern to set
 * @param mode - 'demo' for localStorage, 'live' for API
 */
export async function setOrganisationWorkingPattern(
  pattern: WorkingPattern,
  mode: 'demo' | 'live'
): Promise<WorkingPattern> {
  if (mode === 'live') {
    return await workingPatternsApi.setOrganisationPattern(pattern);
  } else {
    workingPatternService.saveOrgDefault(pattern);
    return pattern;
  }
}


/**
 * Get working pattern for an employee
 * @param employeeId - The employee ID
 * @param mode - 'demo' for localStorage, 'live' for API
 */
export async function getEmployeeWorkingPattern(
  employeeId: number,
  mode: 'demo' | 'live'
): Promise<{
  pattern: WorkingPattern;
  inherited: boolean;
  id?: string;
}> {
  const employeeIdStr = String(employeeId);
  if (mode === 'live') {
    return await workingPatternsApi.getEmployeePattern(employeeIdStr);
  } else {
    const pattern = workingPatternService.getEffectivePattern(employeeIdStr);
    const inherited = !workingPatternService.hasCustomPattern(employeeIdStr);
    return { pattern, inherited };
  }
}

/**
 * Set custom working pattern for an employee
 * @param employeeId - The employee ID
 * @param pattern - The working pattern to set
 * @param mode - 'demo' for localStorage, 'live' for API
 */
export async function setEmployeeWorkingPattern(
  employeeId: number,
  pattern: WorkingPattern,
  mode: 'demo' | 'live'
): Promise<{
  pattern: WorkingPattern;
  inherited: boolean;
  id?: string;
}> {
  const employeeIdStr = String(employeeId);
  if (mode === 'live') {
    return await workingPatternsApi.setEmployeePattern(employeeIdStr, pattern);
  } else {
    workingPatternService.saveEmployeePattern(employeeIdStr, pattern);
    return { pattern, inherited: false };
  }
}

/**
 * Delete custom working pattern for an employee (revert to organisation default)
 * @param employeeId - The employee ID
 * @param patternId - The pattern ID (only used in live mode)
 * @param mode - 'demo' for localStorage, 'live' for API
 */
export async function deleteEmployeeWorkingPattern(
  employeeId: number,
  patternId: string | undefined,
  mode: 'demo' | 'live'
): Promise<void> {
  const employeeIdStr = String(employeeId);
  if (mode === 'live') {
    if (!patternId) {
      throw new Error('Pattern ID is required to delete in live mode');
    }
    await workingPatternsApi.deleteEmployeePattern(employeeIdStr, patternId);
  } else {
    workingPatternService.deleteEmployeePattern(employeeIdStr);
  }
}


/**
 * Get organisation timesheet settings
 * In live mode, also fetches the working pattern from OrganisationWorkingPattern
 * and merges it into the settings as workingProfile
 * @param mode - 'demo' for localStorage, 'live' for API
 */
export async function getOrganisationTimesheetSettings(
  mode: 'demo' | 'live'
): Promise<OrganisationTimesheetSettings & { rowVersion?: string }> {
  if (mode === 'live') {
    // Fetch timesheet settings and working pattern separately to handle failures gracefully
    // The backend doesn't return workingProfile, so we need to fetch it separately
    // from the OrganisationWorkingPattern table and merge it into the settings
    // 
    // IMPORTANT: We use Promise.allSettled instead of Promise.all to ensure that
    // a failure in the working pattern endpoint (e.g., 404 when no pattern is configured)
    // doesn't cause the entire settings fetch to fail. This ensures deadline settings
    // are still applied even if working patterns aren't configured yet.
    const [settingsResult, patternResult] = await Promise.allSettled([
      timesheetSettingsApi.getOrganisationSettings(),
      workingPatternsApi.getOrganisationPattern(),
    ]);
    
    // If timesheet settings fetch failed, we can't proceed - rethrow the error
    if (settingsResult.status === 'rejected') {
      throw settingsResult.reason;
    }
    
    const settings = settingsResult.value;
    
    // If working pattern fetch succeeded, use it; otherwise fall back to default working profile
    // This ensures deadline settings are still applied even if working patterns aren't configured
    let workingProfile: WorkingProfile;
    if (patternResult.status === 'fulfilled') {
      workingProfile = workingPatternToProfile(patternResult.value);
    } else {
      // Log the error for debugging but don't fail the entire settings fetch
      console.warn('Failed to fetch organisation working pattern, using default:', patternResult.reason);
      workingProfile = DEFAULT_TIMESHEET_SETTINGS.workingProfile;
    }
    
    const mergedSettings = {
      ...settings,
      workingProfile,
    };
    
    // Cache the settings so the next render can use the cached value
    // instead of the default 40h
    setCachedLiveOrgSettings(mergedSettings);
    
    return mergedSettings;
  } else {
    const localSettings = seedOnBehalfOfForDemo(getOrgSettingsLocal());
    
    // In case older/demo data doesn't have workingProfile, derive it from the local pattern
    if (!localSettings.workingProfile) {
      const pattern = workingPatternService.getOrgDefault();
      const workingProfile = workingPatternToProfile(pattern);
      return {
        ...localSettings,
        workingProfile,
      };
    }
    
    return localSettings;
  }
}

/**
 * Populate the On Behalf Of relationship lists with mock data until they have been
 * saved at least once, so demo mode opens with example relationships. Seeding is keyed
 * on the section never having been stored, not on the lists being empty, so saved
 * (including deliberately emptied) lists are left alone.
 */
function seedOnBehalfOfForDemo(settings: OrganisationTimesheetSettings): OrganisationTimesheetSettings {
  if (settings.onBehalfOf) return { ...settings, onBehalfOf: normaliseOnBehalfOfSettings(settings.onBehalfOf) };

  return {
    ...settings,
    onBehalfOf: {
      ...DEFAULT_ON_BEHALF_OF_SETTINGS,
      enterTimeForOthersRelationships: MOCK_ENTRY_RELATIONSHIPS,
      approveOnBehalfOfRelationships: MOCK_APPROVAL_RELATIONSHIPS,
    },
  };
}

/**
 * Update organisation timesheet settings
 * @param settings - The settings to update
 * @param rowVersion - Current RowVersion (only used in live mode for concurrency control)
 * @param mode - 'demo' for localStorage, 'live' for API
 */
export async function updateOrganisationTimesheetSettings(
  settings: OrganisationTimesheetSettings,
  rowVersion: string | undefined,
  mode: 'demo' | 'live'
): Promise<OrganisationTimesheetSettings & { rowVersion?: string }> {
  if (mode === 'live') {
    if (rowVersion === undefined) {
      throw new Error('RowVersion is required to update settings in live mode');
    }
    return await timesheetSettingsApi.updateOrganisationSettings(settings, rowVersion);
  } else {
    saveOrgSettingsLocal(settings);
    return settings;
  }
}

/**
 * Update deadline settings only
 * @param deadline - The deadline settings to update
 * @param currentSettings - Current full settings (only used in live mode)
 * @param rowVersion - Current RowVersion (only used in live mode)
 * @param mode - 'demo' for localStorage, 'live' for API
 */
export async function updateDeadlineSettings(
  deadline: TimesheetDeadlineSettings,
  currentSettings: OrganisationTimesheetSettings | undefined,
  rowVersion: string | undefined,
  mode: 'demo' | 'live',
  onBehalfOf?: OnBehalfOfSettings
): Promise<OrganisationTimesheetSettings & { rowVersion?: string }> {
  if (mode === 'live') {
    if (!currentSettings || rowVersion === undefined) {
      throw new Error('Current settings and RowVersion are required to update deadline in live mode');
    }
    const updatedSettings: OrganisationTimesheetSettings = {
      ...currentSettings,
      deadline,
    };
    return await timesheetSettingsApi.updateOrganisationSettings(updatedSettings, rowVersion, onBehalfOf);
  } else {
    return updateDeadlineLocal(deadline);
  }
}

/**
 * Update utilisation settings only
 * @param utilisation - The utilisation settings to update
 * @param currentSettings - Current full settings (only used in live mode)
 * @param rowVersion - Current RowVersion (only used in live mode)
 * @param mode - 'demo' for localStorage, 'live' for API
 */
export async function updateUtilisationSettings(
  utilisation: UtilisationSettings,
  currentSettings: OrganisationTimesheetSettings | undefined,
  rowVersion: string | undefined,
  mode: 'demo' | 'live'
): Promise<OrganisationTimesheetSettings & { rowVersion?: string }> {
  if (mode === 'live') {
    if (!currentSettings || rowVersion === undefined) {
      throw new Error('Current settings and RowVersion are required to update utilisation in live mode');
    }
    const updatedSettings: OrganisationTimesheetSettings = {
      ...currentSettings,
      utilisation,
    };
    return await timesheetSettingsApi.updateOrganisationSettings(updatedSettings, rowVersion);
  } else {
    return updateUtilisationLocal(utilisation);
  }
}

/** Names that can appear in On Behalf Of relationships */
export interface OnBehalfOfPeople {
  /** Workspace users: can act, and can be acted for on either tab */
  people: string[];
  /** Contractors: can only be entered for (Enter tab "Acting For") */
  contractors: string[];
}

const uniqueNames = (names: string[]) => [...new Set(names.map(n => n.trim()).filter(Boolean))];

/**
 * People that can be named in On Behalf Of relationships
 * @param mode - 'demo' for the mock people list, 'live' for the organisation's employees
 */
export async function getOnBehalfOfPeople(mode: 'demo' | 'live'): Promise<OnBehalfOfPeople> {
  if (mode === 'live') {
    const employees = await timesheetSettingsApi.getOnBehalfOfEmployees();
    return {
      people: uniqueNames(employees.filter(e => !e.isContractor).map(e => e.name)),
      contractors: uniqueNames(employees.filter(e => e.isContractor).map(e => e.name)),
    };
  }
  return { people: MOCK_PEOPLE, contractors: [] };
}

/**
 * Relationship rows that are not yet persisted, keyed by person/acting-for name.
 * Row numbers are positions in each list so server errors point at the visible row.
 */
const relationshipKey = (r: Pick<OnBehalfOfRelationship, 'person' | 'actingFor'>) =>
  `${r.person.trim().toLowerCase()}\u0000${r.actingFor.trim().toLowerCase()}`;

function toNewRelationshipImportRows(
  type: OnBehalfOfRelationshipImportRowDto['type'],
  rows: OnBehalfOfRelationship[],
  persisted: OnBehalfOfRelationship[] | undefined,
): OnBehalfOfRelationshipImportRowDto[] {
  const existing = new Set((persisted ?? []).map(relationshipKey));
  return rows
    .map((r, index) => ({ type, person: r.person, actingFor: r.actingFor, rowNumber: index + 1 }))
    .filter(r => !existing.has(relationshipKey(r)));
}

/**
 * Persisted relationship ids whose person/acting-for pair is no longer in the local list,
 * i.e. rows the user removed since the settings were last loaded.
 */
function toRemovedRelationshipIds(
  rows: OnBehalfOfRelationship[],
  persisted: OnBehalfOfRelationship[] | undefined,
): number[] {
  const current = new Set(rows.map(relationshipKey));
  return (persisted ?? [])
    .filter(r => !current.has(relationshipKey(r)))
    .map(r => Number(r.id))
    .filter(id => Number.isInteger(id) && id > 0);
}

/**
 * Update On Behalf Of settings only
 *
 * Live mode imports any new relationships first (validated server-side; nothing is
 * committed if a row fails), then deletes persisted relationships that were removed
 * locally, then saves the toggles/scopes through the settings API.
 *
 * @param onBehalfOf - The On Behalf Of settings to update
 * @param currentSettings - Current full settings as last loaded from the API (only used in live mode)
 * @param rowVersion - Current RowVersion (only used in live mode)
 * @param mode - 'demo' for localStorage, 'live' for API
 */
export async function updateOnBehalfOfSettings(
  onBehalfOf: OnBehalfOfSettings,
  currentSettings: OrganisationTimesheetSettings | undefined,
  rowVersion: string | undefined,
  mode: 'demo' | 'live'
): Promise<OrganisationTimesheetSettings & { rowVersion?: string }> {
  if (mode === 'live') {
    const baseSettings = currentSettings ?? DEFAULT_TIMESHEET_SETTINGS;
    const persisted = baseSettings.onBehalfOf;

    const importRows = [
      ...toNewRelationshipImportRows('entry', onBehalfOf.enterTimeForOthersRelationships, persisted?.enterTimeForOthersRelationships),
      ...toNewRelationshipImportRows('approval', onBehalfOf.approveOnBehalfOfRelationships, persisted?.approveOnBehalfOfRelationships),
    ];
    if (importRows.length > 0) {
      await timesheetSettingsApi.importOnBehalfOfRelationships(importRows);
    }

    const removedIds = [
      ...toRemovedRelationshipIds(onBehalfOf.enterTimeForOthersRelationships, persisted?.enterTimeForOthersRelationships),
      ...toRemovedRelationshipIds(onBehalfOf.approveOnBehalfOfRelationships, persisted?.approveOnBehalfOfRelationships),
    ];
    if (removedIds.length > 0) {
      await timesheetSettingsApi.deleteOnBehalfOfRelationships(removedIds);
    }

    const saved = await timesheetSettingsApi.updateOrganisationSettings(
      baseSettings,
      rowVersion ?? '',
      { ...onBehalfOf, rowVersion: persisted?.rowVersion ?? onBehalfOf.rowVersion },
    );
    setCachedLiveOrgSettings({ ...saved, workingProfile: baseSettings.workingProfile });
    return saved;
  } else {
    return updateOnBehalfOfLocal(onBehalfOf);
  }
}



/**
 * Update data requirements settings (week start day, role, cost centre)
 * @param weekStartDay - The week start day to update
 * @param showRole - Whether to show role field
 * @param requireRole - Whether role is required
 * @param showCostCentre - Whether to show cost centre field
 * @param requireCostCentre - Whether cost centre is required
 * @param currentSettings - Current full settings (only used in live mode)
 * @param rowVersion - Current RowVersion (only used in live mode)
 * @param mode - 'demo' for localStorage, 'live' for API
 */
export async function updateDataRequirementsSettings(
  weekStartDay: 0 | 1 | 2 | 3 | 4 | 5 | 6,
  showRole: boolean,
  requireRole: boolean,
  showCostCentre: boolean,
  requireCostCentre: boolean,
  descriptionType: 'row' | 'daily',
  requireDescription: 'never' | 'always',
  minimumEntryDuration: '6min' | '15min' | '30min' | '1hour',
  timeRounding: 'none' | '15min' | '30min' | '1hour',
  allowNegativeHours: boolean,
  historicalEntryLimit: number,
  allowFutureDatedEntries: 'no' | '1week' | '2weeks' | '4weeks' | 'unlimited',
  currentSettings: OrganisationTimesheetSettings | undefined,
  rowVersion: string | undefined,
  mode: 'demo' | 'live'
): Promise<OrganisationTimesheetSettings & { rowVersion?: string }> {
  if (mode === 'live') {
    if (!currentSettings || rowVersion === undefined) {
      throw new Error('Current settings and RowVersion are required to update data requirements in live mode');
    }
    const updatedSettings: OrganisationTimesheetSettings = {
      ...currentSettings,
      weekStartDay,
      showRole,
      requireRole,
      showCostCentre,
      requireCostCentre,
      descriptionType,
      requireDescription,
      minimumEntryDuration,
      timeRounding,
      allowNegativeHours,
      historicalEntryLimit,
      allowFutureDatedEntries,
    };
    return await timesheetSettingsApi.updateOrganisationSettings(updatedSettings, rowVersion);
  } else {
    const settings = getOrgSettingsLocal();
    const updatedSettings: OrganisationTimesheetSettings = {
      ...settings,
      weekStartDay,
      showRole,
      requireRole,
      showCostCentre,
      requireCostCentre,
      descriptionType,
      requireDescription,
      minimumEntryDuration,
      timeRounding,
      allowNegativeHours,
      historicalEntryLimit,
      allowFutureDatedEntries,
    };
    saveOrgSettingsLocal(updatedSettings);
    return updatedSettings;
  }
}

/**
 * Convert WorkingProfile to WorkingPattern format for the working patterns API
 */
function workingProfileToPattern(workingProfile: WorkingProfile): WorkingPattern {
  return {
    hoursPerWeek: workingProfile.standardHoursPerWeek,
    workingDays: {
      monday: workingProfile.workingDays.monday.isWorkingDay,
      tuesday: workingProfile.workingDays.tuesday.isWorkingDay,
      wednesday: workingProfile.workingDays.wednesday.isWorkingDay,
      thursday: workingProfile.workingDays.thursday.isWorkingDay,
      friday: workingProfile.workingDays.friday.isWorkingDay,
      saturday: workingProfile.workingDays.saturday.isWorkingDay,
      sunday: workingProfile.workingDays.sunday.isWorkingDay,
    },
    dailyHours: {
      monday: workingProfile.workingDays.monday.expectedHours,
      tuesday: workingProfile.workingDays.tuesday.expectedHours,
      wednesday: workingProfile.workingDays.wednesday.expectedHours,
      thursday: workingProfile.workingDays.thursday.expectedHours,
      friday: workingProfile.workingDays.friday.expectedHours,
      saturday: workingProfile.workingDays.saturday.expectedHours,
      sunday: workingProfile.workingDays.sunday.expectedHours,
    },
    effectiveFrom: workingProfile.effectiveFrom,
    notes: workingProfile.notes,
  };
}

/**
 * Convert WorkingPattern to WorkingProfile format for the Settings page
 */
export function workingPatternToProfile(pattern: WorkingPattern): WorkingProfile {
  const workingDayCount = Object.values(pattern.workingDays).filter(Boolean).length;
  const hoursPerDay = workingDayCount > 0 ? pattern.hoursPerWeek / workingDayCount : 0;
  const dailyHours = pattern.dailyHours || {
    monday: pattern.workingDays.monday ? hoursPerDay : 0,
    tuesday: pattern.workingDays.tuesday ? hoursPerDay : 0,
    wednesday: pattern.workingDays.wednesday ? hoursPerDay : 0,
    thursday: pattern.workingDays.thursday ? hoursPerDay : 0,
    friday: pattern.workingDays.friday ? hoursPerDay : 0,
    saturday: pattern.workingDays.saturday ? hoursPerDay : 0,
    sunday: pattern.workingDays.sunday ? hoursPerDay : 0,
  };
  
  return {
    standardHoursPerWeek: pattern.hoursPerWeek,
    workingDays: {
      monday: { isWorkingDay: pattern.workingDays.monday, expectedHours: dailyHours.monday, isCustom: false },
      tuesday: { isWorkingDay: pattern.workingDays.tuesday, expectedHours: dailyHours.tuesday, isCustom: false },
      wednesday: { isWorkingDay: pattern.workingDays.wednesday, expectedHours: dailyHours.wednesday, isCustom: false },
      thursday: { isWorkingDay: pattern.workingDays.thursday, expectedHours: dailyHours.thursday, isCustom: false },
      friday: { isWorkingDay: pattern.workingDays.friday, expectedHours: dailyHours.friday, isCustom: false },
      saturday: { isWorkingDay: pattern.workingDays.saturday, expectedHours: dailyHours.saturday, isCustom: false },
      sunday: { isWorkingDay: pattern.workingDays.sunday, expectedHours: dailyHours.sunday, isCustom: false },
    },
    effectiveFrom: pattern.effectiveFrom,
    notes: pattern.notes,
  };
}

/**
 * Update working hours settings (working profile, policy enforcement, policy limits, and approval screen)
 * @param workingProfile - The working profile to update
 * @param policyViolations - The policy violation settings to update
 * @param policyLimits - The policy limits to update (optional)
 * @param approvalScreen - The approval screen display settings to update
 * @param currentSettings - Current full settings (only used in live mode)
 * @param rowVersion - Current RowVersion (only used in live mode)
 * @param mode - 'demo' for localStorage, 'live' for API
 */
export async function updateWorkingHoursSettings(
  workingProfile: WorkingProfile,
  policyViolations: PolicyViolationSettings,
  policyLimits: PolicyLimits | undefined,
  approvalScreen: ApprovalScreenSettings,
  currentSettings: OrganisationTimesheetSettings | undefined,
  rowVersion: string | undefined,
  mode: 'demo' | 'live',
  publicHolidayCountryDefault?: string | null,
  publicHolidayProject?: string | null,
  publicHolidayCostCentre?: string | null,
): Promise<OrganisationTimesheetSettings & { rowVersion?: string }> {
  if (mode === 'live') {
    if (!currentSettings || rowVersion === undefined) {
      throw new Error('Current settings and RowVersion are required to update working hours in live mode');
    }
    
    // Save working profile to the OrganisationWorkingPattern table via working patterns API
    // This ensures the working hours are persisted and available for dashboard/team members
    const workingPattern = workingProfileToPattern(workingProfile);
    await workingPatternsApi.setOrganisationPattern(workingPattern);
    
    const updatedSettings: OrganisationTimesheetSettings = {
      ...currentSettings,
      workingProfile,
      policyViolations,
      policyLimits,
      approvalScreen,
      ...(publicHolidayCountryDefault !== undefined ? { publicHolidayCountryDefault } : {}),
      ...(publicHolidayProject !== undefined ? { publicHolidayProject } : {}),
      ...(publicHolidayCostCentre !== undefined ? { publicHolidayCostCentre } : {}),
    };
    const saved = await timesheetSettingsApi.updateOrganisationSettings(updatedSettings, rowVersion);
    // Update the localStorage cache so other pages (e.g. Approval Dashboard)
    // immediately see the new policy settings without waiting for the next API fetch
    setCachedLiveOrgSettings({ ...saved, workingProfile });
    return saved;
  } else {
    // For demo mode, also save to the working pattern service
    const workingPattern = workingProfileToPattern(workingProfile);
    workingPatternService.saveOrgDefault(workingPattern);
    
    return updateWorkingHoursLocal(workingProfile, policyViolations, policyLimits, approvalScreen);
  }
}

/**
 * Update policy violation settings
 * @param policyViolations - New policy violation settings
 * @param currentSettings - Current full settings (only used in live mode)
 * @param rowVersion - Current RowVersion (only used in live mode)
 * @param mode - 'demo' for localStorage, 'live' for API
 */
export async function updatePolicyViolationSettings(
  policyViolations: import('@/types/timesheetSettings').PolicyViolationSettings,
  currentSettings: OrganisationTimesheetSettings | undefined,
  rowVersion: string | undefined,
  mode: 'demo' | 'live'
): Promise<OrganisationTimesheetSettings & { rowVersion?: string }> {
  if (mode === 'live') {
    if (!currentSettings || rowVersion === undefined) {
      throw new Error('Current settings and RowVersion are required to update policy violation settings in live mode');
    }
    const updatedSettings: OrganisationTimesheetSettings = {
      ...currentSettings,
      policyViolations,
    };
    return await timesheetSettingsApi.updateOrganisationSettings(updatedSettings, rowVersion);
  } else {
    // For demo mode, update localStorage
    const currentDemoSettings = await getOrganisationTimesheetSettings('demo');
    const updatedSettings: OrganisationTimesheetSettings = {
      ...currentDemoSettings,
      policyViolations,
    };
    localStorage.setItem(prefixKey('demo_organisation_timesheet_settings'), JSON.stringify(updatedSettings));
    return updatedSettings;
  }
}
